import React, { useCallback, useState } from 'react';
import { useArchitectureStore } from '../../store/useArchitectureStore';
import { Upload, X, ArrowRight, Loader2, Download } from 'lucide-react';
import PlanToElevationWizard from './PlanToElevationWizard';

const ArchitectureCanvas: React.FC = () => {
    // Legacy State
    const { sourceImage, setSourceImage, resultImage, isProcessing, resetWorkspace, lastError, setLastError } = useArchitectureStore();

    // Task Engine State
    const { activeTask, updateTaskData, isLassoActive } = useArchitectureStore();

    // RENDER WIZARD IF TASK IS ACTIVE AND IN PROGRESS
    if (activeTask?.type === 'PLAN_TO_ELEVATION' && activeTask.status === 'in_progress') {
        return <PlanToElevationWizard />;
    }

    // Computed State (Prioritize Task Data)
    const currentSource = activeTask?.data?.sourceImage || sourceImage;
    const currentResult = activeTask?.data?.resultImage || resultImage;
    const maskPoints = activeTask?.data?.maskPoints || [];

    // Helper to update state based on context
    const handleSetSource = (img: string | null) => {
        if (activeTask) {
            updateTaskData({ sourceImage: img });
        } else {
            setSourceImage(img);
        }
    };

    const [isDragging, setIsDragging] = useState(false);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    handleSetSource(event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
    }, [activeTask, setSourceImage, updateTaskData]);

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    handleSetSource(event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    // Global Paste Listener
    React.useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            // If we already have a source image, don't overwrite it automatically (up to product decision, but safer not to)
            if (currentSource || !e.clipboardData) return;

            const items = e.clipboardData.items;
            let imageFile: File | null = null;

            for (let i = 0; i < items.length; i++) {
                if (items[i].type.startsWith('image/')) {
                    imageFile = items[i].getAsFile();
                    break;
                }
            }

            if (imageFile) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    if (event.target?.result) {
                        handleSetSource(event.target.result as string);
                    }
                };
                reader.readAsDataURL(imageFile);
            }
        };

        window.addEventListener('paste', handlePaste);
        return () => window.removeEventListener('paste', handlePaste);
    }, [currentSource, handleSetSource]);

    const handleClear = () => {
        handleSetSource(null);
        if (!activeTask) resetWorkspace();
    };

    const handleCanvasClick = (e: React.MouseEvent) => {
        if (!isLassoActive || !activeTask) return;

        // Calculate relative coordinates (0-100)
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        // Add point to maskPoints
        const newPoints = [...maskPoints, { x, y }];
        updateTaskData({ maskPoints: newPoints });
    };

    if (currentSource) {
        return (
            <div className="relative w-full h-full flex items-center justify-center bg-stone-900/50">
                {/* Clear Button */}
                <button
                    onClick={handleClear}
                    className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 hover:bg-red-500/50 text-white transition-colors"
                    title="Clear Canvas"
                >
                    <X size={20} />
                </button>

                {/* Split View Container */}
                <div className="relative flex w-full h-full max-w-6xl p-4 gap-4">
                    {/* Source Image & Interaction Layer */}
                    <div
                        className={`flex-1 relative rounded-2xl overflow-hidden border border-stone-800 bg-stone-950 ${isLassoActive ? 'cursor-crosshair ring-2 ring-indigo-500/50' : ''}`}
                        onClick={handleCanvasClick}
                    >
                        <div className="absolute top-3 left-3 px-3 py-1 bg-black/60 rounded-full text-xs font-mono text-zinc-400 backdrop-blur-sm z-10 pointer-events-none">
                            ORIGINAL
                        </div>
                        <img
                            src={currentSource}
                            alt="Original"
                            className="w-full h-full object-contain pointer-events-none select-none"
                        />

                        {/* Lasso SVG Overlay */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none">
                            {maskPoints.length > 0 && (
                                <>
                                    <polygon
                                        points={maskPoints.map((p: any) => `${p.x}%,${p.y}%`).join(' ')}
                                        fill="rgba(99, 102, 241, 0.2)"
                                        stroke="#6366f1"
                                        strokeWidth="2"
                                        vectorEffect="non-scaling-stroke"
                                    />
                                    {maskPoints.map((p: any, i: number) => (
                                        <circle
                                            key={i}
                                            cx={`${p.x}%`}
                                            cy={`${p.y}%`}
                                            r="4"
                                            fill="white"
                                            stroke="#6366f1"
                                            strokeWidth="2"
                                        />
                                    ))}
                                    {/* Close loop preview line if active */}
                                    {isLassoActive && maskPoints.length > 2 && (
                                        <line
                                            x1={`${maskPoints[maskPoints.length - 1].x}%`}
                                            y1={`${maskPoints[maskPoints.length - 1].y}%`}
                                            x2={`${maskPoints[0].x}%`}
                                            y2={`${maskPoints[0].y}%`}
                                            stroke="#6366f1"
                                            strokeWidth="1"
                                            strokeDasharray="4 2"
                                            className="opacity-50"
                                        />
                                    )}
                                </>
                            )}
                        </svg>
                    </div>

                    {/* Result Image or Placeholder */}
                    <div className="flex-1 relative rounded-2xl overflow-hidden border border-stone-800 bg-stone-950 flex items-center justify-center">
                        <div className="absolute top-3 left-3 px-3 py-1 bg-black/60 rounded-full text-xs font-mono text-orange-400 backdrop-blur-sm z-10">
                            TRANSFORMED
                        </div>

                        {/* Download Button */}
                        {currentResult && (
                            <button
                                onClick={() => {
                                    const link = document.createElement('a');
                                    link.href = currentResult;
                                    link.download = `dotting-generated-${Date.now()}.png`;
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                }}
                                className="absolute top-3 right-3 z-20 p-2 rounded-full bg-black/50 hover:bg-stone-800 text-white hover:text-orange-400 transition-all backdrop-blur-md shadow-lg"
                                title="Download Image"
                            >
                                <Download size={20} />
                            </button>
                        )}

                        {currentResult ? (
                            <img
                                src={currentResult}
                                alt="Generated"
                                className="w-full h-full object-contain"
                            />
                        ) : isProcessing ? (
                            <div className="text-center">
                                <Loader2 className="w-10 h-10 text-orange-500 animate-spin mx-auto mb-4" />
                                <p className="text-zinc-500 text-sm animate-pulse">Designing new facade...</p>
                            </div>
                        ) : (
                            <div className="text-center text-stone-600">
                                <ArrowRight className="w-10 h-10 mx-auto mb-2 opacity-20" />
                                <p className="text-sm">Result will appear here</p>
                            </div>
                        )}

                        {/* Error Overlay */}
                        {lastError && !isProcessing && (
                            <div className="absolute inset-0 bg-stone-950/90 flex flex-col items-center justify-center p-6 text-center z-20">
                                <div className="w-12 h-12 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center mb-4">
                                    <X size={24} />
                                </div>
                                <h3 className="text-white font-bold mb-2">Generation Failed</h3>
                                <p className="text-stone-400 text-sm mb-6 max-w-xs">{lastError}</p>
                                <button
                                    onClick={() => setLastError(null)}
                                    className="px-4 py-2 bg-stone-800 hover:bg-stone-700 text-white rounded-lg text-sm transition-colors"
                                >
                                    Dismiss
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`w-full h-full flex flex-col items-center justify-center transition-colors duration-300 pb-40 md:pb-0 px-4 ${isDragging ? 'bg-stone-800/80' : 'bg-transparent'
                }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="text-center p-6 md:p-10 border-2 border-dashed border-stone-800 rounded-3xl bg-stone-900/30 max-w-lg w-full transition-all hover:border-stone-700 hover:bg-stone-900/50 group">
                <div className="w-20 h-20 bg-stone-800 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-stone-700 transition-colors">
                    <Upload className="w-8 h-8 text-stone-400 group-hover:text-zinc-200" />
                </div>

                <h2 className="text-2xl font-bold text-zinc-200 mb-2">Upload Elevation</h2>
                <p className="text-stone-500 mb-8">Drag & drop your building facade image here,<br />or click to browse</p>

                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileInput}
                    />
                    <span className="px-6 py-3 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-medium transition-colors shadow-lg shadow-orange-900/20">
                        Select File
                    </span>
                </label>
            </div>
        </div>
    );
};

export default ArchitectureCanvas;
